import { useEffect, useMemo, useState } from 'react';
import { blueButtonClass, SmallTitleWithSubSpan } from 'src/components/common/common';
import { getPdfBlob_HRM1060_R01 } from './Api_HRM1060_R01';
import { HRM1060_R01Dto } from './Dto_HRM1060_R01';
import { useHRM1060_R01Store } from './Store_HRM1060_R01';
import { CERT_STS_CANCEL } from './Util_HRM1060_R01';

/** 미리보기와 출력 버튼에 필요한 상위 동작이다. */
interface Props {
    handlePrintClick: () => void; // 대장 출력과 같은 처리 함수
}

/** 선택한 저장 행의 PDF를 A4 미리보기로 보여 준다. @param handlePrintClick 출력 함수 @returns 미리보기 영역 */
export const Preview_HRM1060_R01 = ({ handlePrintClick }: Props) => {
    const { HRM1060_R01List, selectedHRM1060_R01List } = useHRM1060_R01Store(); // 현재 선택과 행
    const selected = useMemo<HRM1060_R01Dto | undefined>(() =>
        selectedHRM1060_R01List.length === 1
            ? HRM1060_R01List.find(row => String(row.valueIndex) === selectedHRM1060_R01List[0])
            : undefined, [HRM1060_R01List, selectedHRM1060_R01List]); // 한 행 선택일 때만 미리보기
    const [pdfUrl, setPdfUrl] = useState(''); // iframe에서 표시할 임시 Blob 주소
    const [hasLoadError, setHasLoadError] = useState(false); // true이면 PDF 요청 실패
    const certNo = selected?.certNo; // 선택한 발급번호
    const printCnt = selected?.printCnt; // 출력 후 다시 불러올 누적 횟수
    const isReady = !!selected && !selected.inserted && !selected.isModified
        && !!certNo && selected.issueSts !== CERT_STS_CANCEL; // true이면 저장·미취소 행

    // 번호 또는 출력 횟수가 바뀌면 PDF를 다시 읽고 이전 Blob 주소를 해제한다.
    useEffect(() => {
        let isActive = true; // true이면 비동기 응답을 현재 선택에 반영
        let objectUrl = ''; // 이 효과가 만든 Blob 주소
        setPdfUrl('');
        setHasLoadError(false);
        if (isReady && certNo) {
            getPdfBlob_HRM1060_R01(certNo)
                .then(blob => {
                    objectUrl = URL.createObjectURL(blob);
                    if (isActive) {
                        setPdfUrl(objectUrl);
                    } else {
                        URL.revokeObjectURL(objectUrl);
                    }
                })
                .catch(() => {
                    if (isActive) setHasLoadError(true);
                });
        }
        return () => {
            isActive = false;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [certNo, printCnt, isReady]); // 선택·출력 횟수·저장 가능 여부 기준

    let guidance = ''; // 미리보기를 표시하지 못할 때의 안내 문구
    if (!selected) {
        guidance = '증명서를 선택하세요.';
    } else if (selected.issueSts === CERT_STS_CANCEL) {
        guidance = '취소된 증명서는 미리보기할 수 없습니다.';
    } else if (!isReady) {
        guidance = '저장 후 미리보기할 수 있습니다.';
    } else if (hasLoadError) {
        guidance = '미리보기를 불러오지 못했습니다.';
    }

    return (
        <section className="flex flex-col h-full min-h-0">
            {/* 공통 제목 테두리 안에 선택 증명서와 작은 출력 버튼을 함께 표시한다. */}
            <SmallTitleWithSubSpan title="출력 미리보기 (A4)" subTitle={
                <span className="inline-flex items-center gap-[6px]">
                    {selected && <span>{selected.certNo} · {selected.certTypeNm}</span>}
                    {/* 액션바 조회 버튼과 같은 색상 클래스를 사용한다. */}
                    <button type="button"
                        className={`h-[22px] shrink-0 px-[8px] text-white rounded-[4px] font-semibold ${blueButtonClass}`}
                        onClick={handlePrintClick}>PDF 출력</button>
                </span>
            } />
            {/* PDF가 준비되면 브라우저 내장 뷰어에 표시하고, 아니면 원인을 안내한다. */}
            <div className="flex-1 min-h-0 border border-gray-300 bg-gray-100">
                {pdfUrl && isReady
                    ? <iframe title="증명서 PDF 미리보기" src={pdfUrl + '#toolbar=0'}
                        className="w-full h-full border-0" />
                    : <div className="w-full h-full flex items-center justify-center text-gray-500">
                        {guidance}
                    </div>}
            </div>
        </section>
    );
};
